import { trackError } from '@billy/track-error'
import { useCallback, useState } from 'react'

import { useOnboardingFormContext } from '../contexts/OnboardingFormContext'
import { useOnboardingSetupContext } from '../contexts/OnboardingSetupContext'
import { OnboardingFinalizedState } from '../enums/onboardingFinalizedState'
import { OnboardingVariant } from '../enums/onboardingVariant'
import { getOnboardingNextStep } from '../utils/getOnboardingNextStep'
import { useTrackOnboarding } from './useTrackOnboarding'

export const useOnboardingNavigation = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { currentStep, finalizeOnboarding, isExtendedQuestionnaire, transitionToStep, variant } =
    useOnboardingSetupContext()
  const { isLoading, submitStep, validateStep } = useOnboardingFormContext()
  const { trackOnboarding } = useTrackOnboarding()

  const isActionDisabled = isLoading || isSubmitting

  const handleContinue = useCallback(async () => {
    if (!currentStep || !variant || variant === OnboardingVariant.Control) {
      return
    }

    trackOnboarding('Continue clicked')

    const isValid = await validateStep()
    if (!isValid) {
      return
    }

    setIsSubmitting(true)
    try {
      await submitStep()

      const nextStep = getOnboardingNextStep({ action: 'continue', currentStep, isExtendedQuestionnaire })

      if (nextStep.type === 'step') {
        transitionToStep(nextStep.step)
      } else {
        finalizeOnboarding({ state: OnboardingFinalizedState.Completed })
      }
    } catch (error) {
      trackError(error)
    } finally {
      setIsSubmitting(false)
    }
  }, [
    currentStep,
    finalizeOnboarding,
    isExtendedQuestionnaire,
    submitStep,
    trackOnboarding,
    transitionToStep,
    validateStep,
    variant,
  ])

  const handleSkip = useCallback(() => {
    if (!currentStep || !variant || variant === OnboardingVariant.Control) {
      return
    }

    trackOnboarding('Skip clicked')

    const nextStep = getOnboardingNextStep({ action: 'skip', currentStep, isExtendedQuestionnaire })

    if (nextStep.type === 'step') {
      transitionToStep(nextStep.step)
    } else {
      finalizeOnboarding({ state: OnboardingFinalizedState.Skipped })
    }
  }, [currentStep, finalizeOnboarding, isExtendedQuestionnaire, trackOnboarding, transitionToStep, variant])

  return { handleContinue, handleSkip, isActionDisabled }
}
